import { PaymentMethod, PaymentType } from "../../Stripe/StripeOrderDTO.ts";
import {
  centsToEuros,
  parseCalendarDate,
  parseEurosToCents,
  parseKnownValue,
  parseKnownValues,
  parseList,
  parsePageIndex,
  parseSort,
  setOrDelete,
} from "../../UI/Table/listUrlParams.ts";
import {
  DEFAULT_ORDERS_SORT,
  FAILURE_COUNT_UNITS,
  FailingInstallmentsRange,
  ORDER_SORT_FIELDS,
  ORDERS_PAGE_SIZE,
  OrdersFilters,
  OrdersListQuery,
} from "./OrdersListQuery.ts";

const FILTER_PARAMS = [
  "status",
  "product",
  "paymentMethod",
  "paymentType",
  "failedBy",
  "failedFrom",
  "failedTo",
  "coupon",
  "createdFrom",
  "createdTo",
  "amountMin",
  "amountMax",
];

function parsePositiveInteger(value: string | null): number | undefined {
  const number = Number(value);
  return value && Number.isInteger(number) && number > 0 ? number : undefined;
}

function parseFailingInstallmentsRange(
  params: URLSearchParams,
): FailingInstallmentsRange | undefined {
  const min = parsePositiveInteger(params.get("failedFrom"));
  const max = parsePositiveInteger(params.get("failedTo"));
  if (min === undefined && max === undefined) return undefined;
  return {
    countBy:
      parseKnownValue(params.get("failedBy"), FAILURE_COUNT_UNITS) ??
      "attempts",
    min,
    max,
  };
}

export function parseOrdersListQuery<Status extends string>(
  params: URLSearchParams,
  knownStatuses: readonly Status[],
): OrdersListQuery<Status> {
  return {
    search: params.get("search") || undefined,
    statuses: parseKnownValues(params.get("status"), knownStatuses),
    productIds: parseList(params.get("product")),
    paymentMethods: parseKnownValues(
      params.get("paymentMethod"),
      Object.values(PaymentMethod),
    ),
    paymentType: parseKnownValue(
      params.get("paymentType"),
      Object.values(PaymentType),
    ),
    failingInstallments: parseFailingInstallmentsRange(params),
    coupon: params.get("coupon") || undefined,
    createdFrom: parseCalendarDate(params.get("createdFrom")),
    createdTo: parseCalendarDate(params.get("createdTo")),
    amountMinInCents: parseEurosToCents(params.get("amountMin")),
    amountMaxInCents: parseEurosToCents(params.get("amountMax")),
    ...parseSort(params.get("sort"), ORDER_SORT_FIELDS, DEFAULT_ORDERS_SORT),
    page: parsePageIndex(params.get("page")),
    pageSize: ORDERS_PAGE_SIZE,
  };
}

export function withOrdersFilters<Status extends string>(
  current: URLSearchParams,
  filters: OrdersFilters<Status>,
): URLSearchParams {
  const params = new URLSearchParams(current);
  FILTER_PARAMS.forEach((param) => params.delete(param));
  setOrDelete(params, "status", filters.statuses?.join(","));
  setOrDelete(params, "product", filters.productIds?.join(","));
  setOrDelete(params, "paymentMethod", filters.paymentMethods?.join(","));
  setOrDelete(params, "paymentType", filters.paymentType);
  const failing = filters.failingInstallments;
  setOrDelete(params, "failedBy", failing?.countBy);
  setOrDelete(params, "failedFrom", failing?.min?.toString());
  setOrDelete(params, "failedTo", failing?.max?.toString());
  setOrDelete(params, "coupon", filters.coupon);
  setOrDelete(params, "createdFrom", filters.createdFrom);
  setOrDelete(params, "createdTo", filters.createdTo);
  setOrDelete(params, "amountMin", centsToEuros(filters.amountMinInCents));
  setOrDelete(params, "amountMax", centsToEuros(filters.amountMaxInCents));
  params.delete("page");
  return params;
}
